// client/utils/socket.js
// ✅ Socket.io Client für Echtzeit-Updates
import { io } from "socket.io-client";

let socket = null;
let listeners = new Map();

// Event-Typen (gleich wie Server)
export const SOCKET_EVENTS = {
  NOTIFICATION_NEW: "notification:new",
  NOTIFICATION_READ: "notification:read",
  NOTIFICATION_COUNT: "notification:count",
  PENDENZ_CREATED: "pendenz:created",
  PENDENZ_UPDATED: "pendenz:updated",
  PENDENZ_ASSIGNED: "pendenz:assigned",
  PROJECT_UPDATED: "project:updated",
  PROJECT_COMMENT: "project:comment",
  SERVICE_UPDATED: "service:updated",
  USER_ONLINE: "user:online",
  USER_OFFLINE: "user:offline",
  SYSTEM_ANNOUNCEMENT: "system:announcement",
};

/**
 * Verbindet zum Socket.io Server
 */
export function connectSocket() {
  if (socket?.connected) return socket;

  const serverUrl = window.location.origin;
  
  socket = io(serverUrl, {
    path: "/socket.io",
    transports: ["websocket", "polling"],
    withCredentials: true,
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 20000,
  });

  socket.on("connect", () => {
    console.log("🔌 Socket connected:", socket.id);
  });

  socket.on("disconnect", (reason) => {
    console.log("🔌 Socket disconnected:", reason);
  });

  socket.on("connect_error", (error) => {
    console.warn("🔌 Socket connection error:", error.message);
  });

  // Alle registrierten Listener neu verbinden
  listeners.forEach((callbacks, event) => {
    callbacks.forEach((callback) => {
      socket.on(event, callback);
    });
  });

  return socket;
}

/**
 * Trennt die Socket-Verbindung
 */
export function disconnectSocket() {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

/**
 * Prüft ob verbunden
 */
export function isConnected() {
  return socket?.connected || false;
}

/**
 * Registriert einen Event-Listener
 * @param {string} event - Event Name
 * @param {Function} callback - Callback Funktion
 * @returns {Function} Unsubscribe Funktion
 */
export function onSocketEvent(event, callback) {
  if (!listeners.has(event)) {
    listeners.set(event, new Set());
  }
  listeners.get(event).add(callback);

  // Falls bereits verbunden, direkt registrieren
  if (socket) {
    socket.on(event, callback);
  }

  // Unsubscribe Funktion
  return () => {
    listeners.get(event)?.delete(callback);
    if (socket) {
      socket.off(event, callback);
    }
  };
}

/**
 * Entfernt alle Listener für ein Event
 */
export function offSocketEvent(event) {
  listeners.delete(event);
  if (socket) {
    socket.off(event);
  }
}

/**
 * Sendet ein Event an den Server
 */
export function emitSocketEvent(event, data) {
  if (socket?.connected) {
    socket.emit(event, data);
    return true;
  }
  return false;
}

/**
 * Ping zum Server (Verbindungstest)
 */
export function pingServer() {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error("Not connected"));
      return;
    }

    const timeout = setTimeout(() => {
      reject(new Error("Ping timeout"));
    }, 5000);

    socket.emit("ping");
    socket.once("pong", (data) => {
      clearTimeout(timeout);
      resolve(data);
    });
  });
}

export default {
  connectSocket,
  disconnectSocket,
  isConnected,
  onSocketEvent,
  offSocketEvent,
  emitSocketEvent,
  pingServer,
  SOCKET_EVENTS,
};
